// `SignUpPage` — US-001 Sign up + US-202 browser-language detection.
//
// DESIGN.md §1.1, §4.1.

import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { ApiError, useSignUp } from '@barel/api-client/react';
import { useLocale, useSetLocale, useT } from '@barel/i18n';
import type { Locale } from '@barel/i18n';
import { Banner, Button, Checkbox, Field, Input } from '@barel/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { AuthLayout } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { PasswordStrengthMeter } from './components/PasswordStrengthMeter';
import { fieldError, localizeError } from './lib/localizeError';
import { notifyExtensionLogin } from './lib/notifyExtensionLogin';
import { signUpFormSchema, type SignUpFormInput } from './lib/schemas';
import { emit } from './lib/telemetry';
import { isPasswordTooWeak, usePasswordStrength } from './lib/usePasswordStrength';

const SUPPORTED_LOCALES: readonly Locale[] = ['fr', 'en'];

function detectBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return 'fr';
  const lang = (navigator.language ?? '').toLowerCase();
  if (lang.startsWith('fr')) return 'fr';
  return 'en';
}

export function SignUpPage() {
  const t = useT('auth');
  const tCommon = useT('common');
  const locale = useLocale();
  const setLocale = useSetLocale();
  const navigate = useNavigate();
  const signUp = useSignUp();

  // Auto-detect locale on first mount (US-202). Only fires once — subsequent
  // toggles via the LocaleToggle take precedence.
  const detectedRef = useRef<boolean>(false);
  useEffect(() => {
    if (detectedRef.current) return;
    detectedRef.current = true;
    const detected = detectBrowserLocale();
    if (SUPPORTED_LOCALES.includes(detected) && detected !== locale) {
      setLocale(detected);
    }
  }, [locale, setLocale]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormInput>({
    resolver: zodResolver(signUpFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
      name: '',
      locale,
      acceptTerms: undefined as unknown as true,
    },
  });

  // Keep the form's `locale` field in sync with the active locale toggle so
  // the submission payload reflects the last user choice.
  useEffect(() => {
    setValue('locale', locale, { shouldDirty: false, shouldValidate: false });
  }, [locale, setValue]);

  const password = watch('password') ?? '';
  const email = watch('email') ?? '';
  const name = watch('name') ?? '';
  const userInputs = useMemo(() => [email, name].filter(Boolean), [email, name]);
  const strength = usePasswordStrength(password, userInputs);

  const [bannerError, setBannerError] = useState<string | null>(null);
  const [emailTakenInfo, setEmailTakenInfo] = useState<boolean>(false);

  const onSubmit: SubmitHandler<SignUpFormInput> = async (values) => {
    setBannerError(null);
    setEmailTakenInfo(false);
    emit({ name: 'auth_signup_submitted', props: { locale: values.locale } });

    // Client-side strength gate (DESIGN O1: zxcvbn score ≥ 2). The analyzer
    // may not have loaded yet (no keystrokes); in that case we let the
    // server be the gate.
    if (isPasswordTooWeak(strength.score)) {
      setError('password', { message: t('signUp.errors.weakPassword') });
      return;
    }

    try {
      const result = await signUp.mutateAsync({
        email: values.email,
        password: values.password,
        name: values.name,
        locale: values.locale,
      });
      emit({ name: 'auth_signup_succeeded' });
      // Better Auth establishes a session on /sign-up/email even when email
      // verification is still pending — so the browser's cookie identity has
      // flipped to the new user. Notify the extension so its cached Bearer
      // (pointing at a different prior account, if any) is revoked + the
      // popup's `pairedAccount` cleared. Symmetrical with `useSignOut` posting
      // `ls.logout` from UserCardDropdown. The SW compares userIds and no-ops
      // when nothing is paired (fresh browser profile).
      if (result?.user?.id) {
        notifyExtensionLogin(result.user.id);
      }
      void navigate({
        to: '/auth/verify-pending',
        search: { email: values.email },
      });
    } catch (err) {
      if (ApiError.is(err)) {
        if (err.code === 'auth.email_taken' || err.httpStatus === 409) {
          setError('email', { message: t('signUp.errors.emailTaken') });
          setEmailTakenInfo(true);
          return;
        }
        if (err.code === 'auth.weak_password') {
          setError('password', { message: t('signUp.errors.weakPassword') });
          return;
        }
      }
      setBannerError(t('signUp.errors.generic'));
    }
  };

  const formId = useId();
  const titleId = `${formId}-title`;

  // Localized field-error helpers — react-hook-form's resolver runs zod and
  // surfaces the issue's `.message` (English) by default; we re-resolve the
  // localized copy here.
  const nameError = errors.name
    ? (localizeError(
        {
          code: 'too_small',
          minimum: 1,
          path: ['name'],
          message: t('signUp.errors.nameRequired'),
          inclusive: true,
          type: 'string',
        },
        t,
      ) ?? t('signUp.errors.nameRequired'))
    : undefined;
  const emailError = fieldError(errors.email, t('fieldErrors.email.invalid'));
  const passwordError = fieldError(
    errors.password,
    t('fieldErrors.password.tooShort', { min: 10 }),
  );
  const termsError = errors.acceptTerms ? t('signUp.errors.termsRequired') : undefined;

  return (
    <AuthLayout brandHeadline="brandHeadlineSignup">
      <section data-route="auth/signup" aria-labelledby={titleId} className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <h1 id={titleId} className="text-xl font-semibold text-primary">
            {t('signUp.title')}
          </h1>
          <p className="text-sm text-secondary">{t('signUp.subtitle')}</p>
        </header>

        {bannerError ? <Banner tone="error">{bannerError}</Banner> : null}
        {emailTakenInfo ? (
          <Banner tone="info">
            {t('signUp.errors.emailTakenHint')}{' '}
            <Link to="/auth/login" className="underline">
              {t('signUp.signInLink')}
            </Link>
          </Banner>
        ) : null}

        <form
          aria-busy={isSubmitting || undefined}
          className="flex flex-col gap-4"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          data-testid="signup-form"
        >
          <Field invalid={!!nameError} required>
            <Field.Label>{t('signUp.fields.name.label')}</Field.Label>
            <Field.Control>
              <Input
                {...register('name')}
                type="text"
                autoComplete="name"
                placeholder={t('signUp.fields.name.placeholder')}
                maxLength={120}
                data-testid="signup-name"
              />
            </Field.Control>
            {nameError ? <Field.Error>{nameError}</Field.Error> : null}
          </Field>

          <Field invalid={!!emailError} required>
            <Field.Label>{t('signUp.fields.email.label')}</Field.Label>
            <Field.Control>
              <Input
                {...register('email')}
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('signUp.fields.email.placeholder')}
                data-testid="signup-email"
              />
            </Field.Control>
            {emailError ? <Field.Error>{emailError}</Field.Error> : null}
          </Field>

          <Field invalid={!!passwordError} required>
            <Field.Label>{t('signUp.fields.password.label')}</Field.Label>
            <Field.Control>
              <PasswordField
                {...register('password')}
                autoComplete="new-password"
                data-testid="signup-password"
                toggleShowLabel={t('signUp.fields.password.toggleShow')}
                toggleHideLabel={t('signUp.fields.password.toggleHide')}
                toggleTestId="signup-password-toggle"
              />
            </Field.Control>
            {passwordError ? (
              <Field.Error>{passwordError}</Field.Error>
            ) : (
              <Field.Hint>{t('signUp.fields.password.help')}</Field.Hint>
            )}
          </Field>

          {password.length > 0 ? (
            <PasswordStrengthMeter score={strength.score} ready={strength.ready} />
          ) : null}

          <Field invalid={!!termsError} required>
            <label className="flex items-start gap-2 text-sm text-secondary">
              <Checkbox
                {...register('acceptTerms')}
                onCheckedChange={(value) => {
                  // RHF needs a real boolean; CheckedState may be 'indeterminate'.
                  setValue('acceptTerms', value === true ? true : (false as unknown as true), {
                    shouldValidate: true,
                  });
                }}
                aria-label={t('signUp.termsAccept')}
                data-testid="signup-terms"
              />
              <span>{t('signUp.termsAccept')}</span>
            </label>
            {termsError ? <Field.Error>{termsError}</Field.Error> : null}
          </Field>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isSubmitting}
            data-testid="signup-submit"
          >
            {t('signUp.submit')}
          </Button>
        </form>

        <p className="text-sm text-secondary">
          {t('signUp.hasAccount')}{' '}
          <Link to="/auth/login" className="font-medium text-primary underline">
            {t('signUp.signInLink')}
          </Link>
        </p>

        <span className="sr-only" aria-live="polite">
          {isSubmitting ? tCommon('states.loading') : ''}
        </span>
      </section>
    </AuthLayout>
  );
}
